/**
 * warranty.js
 * Handles warranty selection, cart bundling, and auto-removal.
 * No dependencies — vanilla JS only.
 */

const Warranty = (() => {

  // ─── Constants ────────────────────────────────────────────────────────────
  const CART_API     = '/cart';
  const PRODUCTS_API = '/products';
  const PROP_KEY     = '_warranty_for';     // line item property linking warranty → parent
  const PROP_LABEL   = 'Extended Warranty'; // shown on order confirmation / packing slip

  // ─── State ────────────────────────────────────────────────────────────────
  let selectedVariantId = null;  // currently selected warranty variant ID
  let warrantyVariants  = [];    // fetched variant objects [ { id, title, price } ]


  // ─── Product page init ────────────────────────────────────────────────────
  function initProductPage() {
    const selector = document.querySelector('.warranty-selector');
    if (!selector) return;

    const warrantyHandle = selector.dataset.warrantyHandle;
    if (!warrantyHandle) return;

    fetchWarrantyProduct(warrantyHandle, selector);
  }


  // ─── Cart page init ───────────────────────────────────────────────────────
  function initCartPage() {
    if (window.location.pathname !== '/cart') return;

    observeCartChanges();
    interceptCartForm();
  }


  // ─── Fetch warranty product variants ──────────────────────────────────────
  async function fetchWarrantyProduct(handle, selector) {
    const loading = selector.querySelector('.warranty-selector__loading');
    const options = selector.querySelector('.warranty-selector__options');
    const error   = selector.querySelector('.warranty-selector__error');

    try {
      const productRes = await fetch(`${PRODUCTS_API}/${handle}.js`);

      if (!productRes.ok) throw new Error(`Product fetch failed: ${productRes.status}`);

      const productData = await productRes.json();

      warrantyVariants = productData.variants.map(v => ({
        id       : v.id,
        title    : v.title,
        price    : v.price,
        available: v.available
      }));

      if (!warrantyVariants.length) throw new Error('No variants found');

      renderVariants(selector, warrantyVariants);
      bindEvents(selector);

      loading.style.display = 'none';
      options.style.display = 'block';

    } catch (e) {
      console.error('[Warranty]', e);
      loading.style.display = 'none';
      error.style.display   = 'block';
    }
  }


  // ─── Render variant buttons ────────────────────────────────────────────────
  function renderVariants(selector, variants) {
    const group = selector.querySelector('.warranty-selector__duration-group');
    group.innerHTML = '';

    variants.forEach((variant, i) => {
      const btn = document.createElement('button');
      btn.type                 = 'button';
      btn.className            = 'warranty-selector__duration-btn';
      btn.dataset.variantId    = variant.id;
      btn.dataset.variantPrice = variant.price;
      btn.setAttribute('aria-pressed', i === 0 ? 'true' : 'false');

      btn.innerHTML = `
        <span class="warranty-selector__duration-label">${variant.title}</span>
        <span class="warranty-selector__duration-price">${formatMoney(variant.price)}</span>
      `;

      if (i === 0) {
        btn.classList.add('is-selected');
        selectedVariantId = variant.id;
      }

      group.appendChild(btn);
    });
  }


  // ─── Bind UI events ────────────────────────────────────────────────────────
  function bindEvents(selector) {
    const checkbox     = selector.querySelector('.warranty-selector__checkbox');
    const variantsPane = selector.querySelector('.warranty-selector__variants');
    const group        = selector.querySelector('.warranty-selector__duration-group');

    // Checkbox toggle — show/hide variant picker
    checkbox.addEventListener('change', () => {
      variantsPane.style.display = checkbox.checked ? 'block' : 'none';
      if (!checkbox.checked) {
        selectedVariantId = warrantyVariants[0]?.id || null;
        updateButtonSelection(group, selectedVariantId);
      }
    });

    // Duration button selection
    group.addEventListener('click', (e) => {
      const btn = e.target.closest('.warranty-selector__duration-btn');
      if (!btn) return;
      selectedVariantId = parseInt(btn.dataset.variantId, 10);
      updateButtonSelection(group, selectedVariantId);
    });

    // Intercept the main product form submission
    interceptAddToCart(checkbox);
  }


  // ─── Intercept ATC form ────────────────────────────────────────────────────
  function interceptAddToCart(checkbox) {
    const productForm = document.querySelector('form[action="/cart/add"]');
    if (!productForm) return;

    productForm.addEventListener('submit', async (e) => {
      if (!checkbox.checked || !selectedVariantId) return; // no warranty, proceed normally

      e.preventDefault();

      const formData        = new FormData(productForm);
      const parentVariantId = formData.get('id');

      try {
        // 1. Add the main product
        const quantity = parseInt(formData.get('quantity'), 10) || 1;

        await addToCart(parentVariantId, quantity, {});

        // 2. Fetch cart to get the actual line item key for the parent
        const cart       = await getCart();
        const parentLine = cart.items.find(
          item => String(item.variant_id) === String(parentVariantId)
               && !item.properties?.[PROP_KEY]
        );

        if (!parentLine) throw new Error('Could not find parent line item after adding');

        // 3. Add warranty linked by line item key
        await addToCart(selectedVariantId, 1, {
          [PROP_KEY]: parentLine.key,
          'Warranty': PROP_LABEL
        });

        window.location.href = '/cart';

      } catch (err) {
        console.error('[Warranty] ATC error:', err);
        productForm.submit();
      }
    });
  }


  // ─── Cart API helpers ──────────────────────────────────────────────────────
  async function addToCart(variantId, quantity, properties) {
    const res = await fetch(`${CART_API}/add.js`, {
      method  : 'POST',
      headers : { 'Content-Type': 'application/json' },
      body    : JSON.stringify({ id: variantId, quantity, properties })
    });
    if (!res.ok) throw new Error(`Add to cart failed: ${res.status}`);
    return res.json();
  }

  async function getCart() {
    const res = await fetch(`${CART_API}.js`);
    return res.json();
  }

  async function removeLineItem(lineKey) {
    const res = await fetch(`${CART_API}/change.js`, {
      method  : 'POST',
      headers : { 'Content-Type': 'application/json' },
      body    : JSON.stringify({ id: lineKey, quantity: 0 })
    });
    if (!res.ok) throw new Error(`Remove failed: ${res.status}`);
    return res.json();
  }


  // ─── Auto-remove warranty when parent is removed ───────────────────────────
  function observeCartChanges() {
    document.addEventListener('click', (e) => {
      const link = e.target.closest('a[href*="quantity=0"]');
      if (!link) return;

      e.preventDefault();
      e.stopImmediatePropagation();

      handleRemoveClick(link);
    }, true); // capture phase — fires before browser processes the link
  }

  async function handleRemoveClick(link) {
  try {
    // Extract the line key from the href and remove the parent via API first
    const url    = new URL(link.href, window.location.origin);
    const lineId = url.searchParams.get('id');

    if (lineId) {
      await removeLineItem(lineId);        // remove parent first
      await syncWarrantiesAfterUpdate();   // now orphaned warranty is detected & removed
      window.location.href = '/cart';      // reload cart cleanly
    } else {
      window.location.href = link.href;    // fallback
    }
  } catch (err) {
    console.error('[Warranty] Remove sync error:', err);
    window.location.href = link.href;
  }
}

  async function syncWarrantiesAfterUpdate() {
    const cart  = await getCart();
    const items = cart.items;

    // All line keys currently in cart that are NOT warranties themselves
    const parentLineKeys = new Set(
      items
        .filter(item => !item.properties?.[PROP_KEY])
        .map(item => item.key)
    );

    // Warranties whose parent line key is no longer in the cart
    const orphaned = items.filter(item => {
      const parentKey = item.properties?.[PROP_KEY];
      return parentKey && !parentLineKeys.has(parentKey);
    });

    for (const warranty of orphaned) {
      await removeLineItem(warranty.key);
    }
  }


  // ─── UI helpers ───────────────────────────────────────────────────────────
  function updateButtonSelection(group, variantId) {
    group.querySelectorAll('.warranty-selector__duration-btn').forEach(btn => {
      const isSelected = parseInt(btn.dataset.variantId, 10) === variantId;
      btn.classList.toggle('is-selected', isSelected);
      btn.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    });
  }

  function formatMoney(cents) {
    return new Intl.NumberFormat('en-US', {
      style   : 'currency',
      currency: 'USD'
    }).format(cents / 100);
  }

  function interceptCartForm() {
  const cartForm = document.querySelector('form[action="/cart"]');
  if (!cartForm) return;

  cartForm.addEventListener('submit', async (e) => {
    // If the checkout button triggered this, let it pass through normally
    if (e.submitter && e.submitter.name === 'checkout') return;

    e.preventDefault();

    const formData = new FormData(cartForm);

    try {
      await fetch('/cart', {
        method: 'POST',
        body: formData
      });

      await syncWarrantiesAfterUpdate();

    } catch (err) {
      console.error('[Warranty] Cart update error:', err);
    }

    window.location.href = '/cart';
  });

}


  // ─── Public API ───────────────────────────────────────────────────────────
  return { initProductPage, initCartPage };

})();

document.addEventListener('DOMContentLoaded', () => {
  Warranty.initProductPage();
  Warranty.initCartPage();
});
